import { randomToken } from "./pkce";

/**
 * The tabs of one sign-in. A duplicated tab, a reopened closed tab and a restored browser session all copy
 * sessionStorage, so they hold the same refresh token as the tab they came from, and a sign-out in any of them (which
 * revokes that token) ends the session for all of them. They agree, over a same-origin BroadcastChannel, on what they
 * must share: the time of the latest input, a hold for work in progress, and the sign-out itself.
 *
 * A sign-in's tabs are recognised by a random group id kept with the idle clock in sessionStorage (`IDLE_RECORD_KEY`
 * in idle.ts), so a tab signed in separately, or as someone else, is never affected. Messages carry that id, a random
 * id for the page and times only: never a token, a name or anything about a patient. Where BroadcastChannel is missing
 * every tab simply keeps to itself.
 */
export const TAB_CHANNEL_NAME = "a123.auth.tabs";

/** This page's id, so a tab ignores its own messages (two channels with one name in one page hear each other). */
export const THIS_TAB = randomToken(12);

export type TabMessageBody =
  /** A tab joining, or about to sign out for inactivity, asks the others for their latest input and holds. */
  | { kind: "hello" }
  /** Epoch ms of the latest user input in the sending tab. */
  | { kind: "activity"; at: number }
  /** The sending tab's hold on the idle sign-out (epoch ms), or null when it has none. */
  | { kind: "hold"; until: number | null }
  /** The sending tab has signed out; `idle` when it was for inactivity. */
  | { kind: "signed-out"; idle: boolean };

export type TabMessage = TabMessageBody & { group: string; tab: string };

export interface TabChannel {
  /** Sends to this sign-in's other tabs; does nothing when this tab has no group (signed out, or no storage). */
  post(body: TabMessageBody): void;
  /** Messages from this sign-in's other tabs only. */
  subscribe(listener: (message: TabMessage) => void): () => void;
  close(): void;
}

/** The part of BroadcastChannel this uses. */
export interface ChannelLike {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

const isTime = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** Same origin is not the same as trusted shape: anything malformed is dropped. */
export function isTabMessage(v: unknown): v is TabMessage {
  if (!v || typeof v !== "object") return false;
  const m = v as Record<string, unknown>;
  if (typeof m.group !== "string" || !m.group || typeof m.tab !== "string" || !m.tab) return false;
  switch (m.kind) {
    case "hello":
      return true;
    case "activity":
      return isTime(m.at);
    case "hold":
      return m.until === null || isTime(m.until);
    case "signed-out":
      return typeof m.idle === "boolean";
    default:
      return false;
  }
}

function browserChannel(name: string): ChannelLike | null {
  try {
    return typeof BroadcastChannel === "undefined" ? null : (new BroadcastChannel(name) as unknown as ChannelLike);
  } catch {
    return null;
  }
}

/**
 * Opens the channel. `group` is read at every send and receive (it changes at sign-in and disappears at sign-out), so
 * a channel opened before the group exists still works once it does.
 */
export function openTabChannel(options: { group: () => string | null; tabId?: string; create?: (name: string) => ChannelLike | null }): TabChannel {
  const tab = options.tabId ?? THIS_TAB;
  const channel = (options.create ?? browserChannel)(TAB_CHANNEL_NAME);
  const listeners = new Set<(message: TabMessage) => void>();
  if (channel) {
    channel.onmessage = (event) => {
      const m = event.data;
      if (!isTabMessage(m) || m.tab === tab || m.group !== options.group()) return;
      for (const l of [...listeners]) l(m);
    };
  }
  return {
    post(body) {
      const group = options.group();
      if (!channel || !group) return;
      try {
        channel.postMessage({ ...body, group, tab });
      } catch {
        /* a closed channel: nothing to tell */
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close() {
      listeners.clear();
      if (channel) {
        channel.onmessage = null;
        channel.close();
      }
    },
  };
}
